import * as turf from "@turf/turf";
import {
  activeAdvancedParts,
  hasAttributePredicate,
  isConditionActive,
  roiPolygon4326,
} from "./advancedFilters";

const typedValue = (value, type) => {
  if (value == null) return value;
  if (type === "number") return Number(value);
  if (type === "boolean") return value === true || String(value).toLowerCase() === "true";
  if (type === "date") return new Date(value).getTime();
  return String(value);
};

const likeRegex = pattern => {
  const escaped = String(pattern).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  return new RegExp(`^${escaped.replace(/%/g, ".*").replace(/_/g, ".")}$`);
};

export const evaluateImportedCondition = (row, condition, schema = {}) => {
  const value = row[condition.field];
  const operator = condition.operator || "eq";
  if (operator === "isNull") return value == null;
  if (operator === "isNotNull" || operator === "notNull") return value != null;
  if (operator === "in") {
    const actual = typedValue(value, schema[condition.field]);
    return (condition.value || []).some(candidate => actual === typedValue(candidate, schema[condition.field]));
  }
  if (value == null) return false;
  if (["contains", "ilike"].includes(operator)) {
    return String(value).toLocaleLowerCase().includes(String(condition.value).toLocaleLowerCase());
  }
  if (operator === "like") return likeRegex(condition.value).test(String(value));

  const actual = typedValue(value, schema[condition.field]);
  const expected = typedValue(condition.value, schema[condition.field]);
  if (operator === "eq") return actual === expected;
  if (operator === "neq") return actual !== expected;
  if (operator === "gt") return actual > expected;
  if (operator === "gte") return actual >= expected;
  if (operator === "lt") return actual < expected;
  if (operator === "lte") return actual <= expected;
  return false;
};

export const evaluateImportedTree = (row, node, schema = {}) => {
  if (!node) return true;
  if (node.kind === "condition") return evaluateImportedCondition(row, node, schema);
  const children = (node.children || []).filter(hasAttributePredicate);
  if (!children.length) return true;
  if (node.match === "all") return children.every(child => evaluateImportedTree(row, child, schema));
  if (node.match === "none") return !children.some(child => evaluateImportedTree(row, child, schema));
  return children.some(child => evaluateImportedTree(row, child, schema));
};

const quickTree = quick => ({
  kind: "group",
  match: "all",
  children: Object.entries(quick || {})
    .filter(([, condition]) => isConditionActive(condition))
    .map(([field, condition]) => ({ ...condition, kind: "condition", field })),
});

const matchesRoi = (row, spec) => {
  if (!row.geometry) return false;
  const rings = roiPolygon4326(spec);
  if (!rings || !rings.length) return true;
  try {
    const area = turf.polygon(rings);
    const feature = turf.toWgs84(turf.feature(row.geometry));
    if (spec.operation === "featureWithinArea") return turf.booleanWithin(feature, area);
    if (spec.operation === "featureContainsArea") return turf.booleanContains(feature, area);
    return turf.booleanIntersects(feature, area);
  } catch (error) {
    return false;
  }
};

// matchesCrossLayer (PB-24_v4): crossLayerArtifact.geometries es una lista
// de partes de la capa objetivo, ya reparadas por-feature pero SIN unir
// entre sí (ver attributesTable.js::prepareCrossLayer) — probar "alguna
// parte cumple" con .some() es equivalente a probar contra la unión
// completa para "intersects" siempre, y para "dentro-de"/"contiene" cuando
// el origen es garantizado Point o no cruza el borde compartido entre dos
// partes vecinas del target (trade-off aceptado explícitamente, ver plan
// PB-24_v4 — no aplica a Capa_Terreno, que nunca llega por acá).
const matchesCrossLayer = (row, crossLayerArtifact, operation) => {
  if (!crossLayerArtifact) return true;
  if (crossLayerArtifact.empty) return false;
  if (!row.geometry || !crossLayerArtifact.geometries || !crossLayerArtifact.geometries.length) return false;
  try {
    const feature = turf.feature(row.geometry);
    return crossLayerArtifact.geometries.some(targetGeometry => {
      const target = turf.feature(targetGeometry);
      if (operation === "sourceWithinTarget") return turf.booleanWithin(feature, target);
      if (operation === "sourceContainsTarget") return turf.booleanContains(feature, target);
      return turf.booleanIntersects(feature, target);
    });
  } catch (error) {
    return false;
  }
};

export const filterImportedRecords = (records, appliedFilter, schema = {}, crossLayerArtifact = null) => {
  const filter = appliedFilter || { mode: "none" };
  let attributeTree = null;
  let roi = null;
  let crossLayer = null;
  if (filter.mode === "quick") attributeTree = quickTree(filter.quick);
  if (filter.mode === "advanced") {
    const parts = activeAdvancedParts(filter);
    attributeTree = parts.attribute;
    roi = parts.roi;
    crossLayer = parts.crossLayer;
  }
  return (records || []).filter(record =>
    (!attributeTree || evaluateImportedTree(record.row, attributeTree, schema)) &&
    (!roi || matchesRoi(record.row, roi)) &&
    (!crossLayer || matchesCrossLayer(record.row, crossLayerArtifact, crossLayer.operation))
  );
};
